import type { DwtfAuth } from "../core/auth.js";
import { sdkAssetUrl } from "../assets.js";

type Sess = { userId: string; token: string };

export type SdkConfig = {
  analytics?: boolean;
  push?: boolean;
  support?: boolean;
  brand?: string;
};

type G = {
  DWTF?: {
    DwtfAnalytics?: new (c: { appId: string; apiUrl?: string }) => unknown;
    DwtfPush?: new (c: { apiUrl?: string }) => {
      subscribe: (id: string, t: string) => Promise<unknown>;
    };
    mountSupportWidget?: (o: { auth: DwtfAuth }) => () => void;
  };
};

function loadJs(src: string): Promise<void> {
  return new Promise((ok, err) => {
    const el = document.createElement("script");
    el.src = src;
    el.onload = () => ok();
    el.onerror = () => err(new Error("mod"));
    document.head.appendChild(el);
  });
}

type Boot = { run: (u: Sess) => void; cfg?: SdkConfig };
const boots: Record<string, Promise<Boot>> = {};

/** Fetch public flags and load siblings from sdkDir(). Once per appId. Fail-open. ADR 0010 / 0011. */
export function bootModules(
  auth: DwtfAuth,
  onAuthed: (fn: (u: Sess) => void) => void,
  onConfig?: (c: SdkConfig) => void,
): void {
  const k = auth.appId;
  if (!boots[k]) {
    boots[k] = (async () => {
      const empty: Boot = { run: (_u: Sess) => {} };
      try {
        const r = await fetch(`${auth.apiUrl}/v1/apps/${k}/sdk-config`);
        if (!r.ok) return empty;
        const c = (await r.json()) as SdkConfig;
        const g = globalThis as G;
        const q: Array<(u: Sess) => void> = [];
        if (c.analytics) {
          if (!g.DWTF?.DwtfAnalytics) await loadJs(sdkAssetUrl("dwtf.analytics.js"));
          if (g.DWTF?.DwtfAnalytics) new g.DWTF.DwtfAnalytics({ appId: k, apiUrl: auth.apiUrl });
        }
        if (c.push) {
          if (!g.DWTF?.DwtfPush) await loadJs(sdkAssetUrl("dwtf.push.js"));
          const Push = g.DWTF?.DwtfPush;
          if (Push) {
            const p = new Push({ apiUrl: auth.apiUrl });
            q.push((u) => {
              void p.subscribe(u.userId, u.token).catch((e) => {
                console.warn("[d.wtf] push subscribe failed", e);
              });
            });
          }
        }
        if (c.support) {
          if (!g.DWTF?.mountSupportWidget) await loadJs(sdkAssetUrl("dwtf.support.js"));
          const m = g.DWTF?.mountSupportWidget;
          if (m) {
            const had = !!auth.getSession();
            m({ auth });
            if (!had) q.push(() => m({ auth }));
          }
        }
        const run = (u: Sess) => {
          for (const f of q) f(u);
        };
        const s = auth.getSession();
        if (s) run(s);
        return { run: q.length ? run : empty.run, cfg: c };
      } catch {
        return empty;
      }
    })();
  }
  void boots[k].then((b) => {
    if (b.cfg) onConfig?.(b.cfg);
    onAuthed(b.run);
  });
}
